/**
 * Dynamic Sections Tracker Service
 * Tracks which dynamic sections (currently HOT_AND_COLD and TOP_WIN_GAMES) were
 * delivered by the lobby settings fetch and whether each is *active* — i.e. the
 * BE returned enough data to actually render the section past its configured
 * minLimit / minGamesAmount threshold.
 *
 * Sections that aren't configured for the operator at all simply do not appear
 * in the emitted attributes — only configured sections are recorded.
 *
 * Tracks:
 * - egt.lobby.dynamic_sections.names: Comma-separated list of recorded section names
 * - egt.lobby.dynamic_sections.<NAME>.active: 1 if active, 0 otherwise
 */

class DynamicSectionsTracker {
    private static instance: DynamicSectionsTracker;
    // Idempotent by name — refetches during a session overwrite the prior state.
    private sections: Map<string, number> = new Map();

    private constructor() {}

    public static getInstance(): DynamicSectionsTracker {
        if (!DynamicSectionsTracker.instance) {
            DynamicSectionsTracker.instance = new DynamicSectionsTracker();
        }
        return DynamicSectionsTracker.instance;
    }

    /**
     * Record (or overwrite) the active state for a dynamic section.
     * @param name - The section name (e.g. 'HOT_AND_COLD', 'TOP_WIN_GAMES')
     * @param active - Whether the section has enough data to render
     */
    public recordSection(name: string, active: boolean): void {
        this.sections.set(name, active ? 1 : 0);

        if (process.env['NODE_ENV'] === 'development') {
            console.log(`[DynamicSectionsTracker] ${name} active=${active ? 1 : 0}`);
        }
    }

    /**
     * Get dynamic-section data formatted for Sentry.
     * Flattened to primitives — Sentry span attributes don't accept arrays of
     * objects. One comma-joined names list plus one active flag per section.
     */
    public getFormattedData(): Record<string, string | number> {
        const formatted: Record<string, string | number> = {};

        if (this.sections.size === 0) {
            return formatted;
        }

        const names: string[] = [];
        this.sections.forEach((active, name) => {
            names.push(name);
            formatted[`egt.lobby.dynamic_sections.${name}.active`] = active;
        });

        formatted['egt.lobby.dynamic_sections.names'] = names.join(',');

        return formatted;
    }

    /**
     * Get the current recorded sections (for debugging)
     */
    public getSections(): Record<string, number> {
        const out: Record<string, number> = {};
        this.sections.forEach((active, name) => {
            out[name] = active;
        });
        return out;
    }

    /**
     * Clear recorded sections (useful for new sessions)
     */
    public clear(): void {
        this.sections.clear();
    }
}

export const dynamicSectionsTracker = DynamicSectionsTracker.getInstance();
